/**
 * Helper function to draw rounded rectangle on canvas
 */
const drawRoundedRect = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
) => {
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + width - radius, y);
  ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
  ctx.lineTo(x + width, y + height - radius);
  ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
  ctx.lineTo(x + radius, y + height);
  ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
  ctx.lineTo(x, y + radius);
  ctx.quadraticCurveTo(x, y, x + radius, y);
  ctx.closePath();
};

/**
 * Composites a QR code onto an image at the bottom left corner
 * with rounded corners and semi-transparent background
 * @param imageUrl - The image URL to composite
 * @param qrCodeDataUrl - The QR code as a base64 data URL
 * @returns Promise that resolves to the composited image as base64 data URL
 */
export const compositeImageWithQR = async (
  imageUrl: string,
  qrCodeDataUrl: string,
): Promise<string> => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';

    img.onload = () => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');

      if (!ctx) {
        reject(new Error('Could not get canvas context'));
        return;
      }

      canvas.width = img.width;
      canvas.height = img.height;

      ctx.drawImage(img, 0, 0);

      const qrImg = new Image();
      qrImg.onload = () => {
        const qrSize = Math.min(img.width * 0.15, img.height * 0.15, 300);
        const padding = qrSize * 0.1;
        const qrWithPadding = qrSize + padding * 2;
        const cornerRadius = qrSize * 0.1;

        const x = padding;
        const y = img.height - qrWithPadding - padding;

        ctx.save();

        ctx.globalAlpha = 0.8;
        ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
        drawRoundedRect(ctx, x, y, qrWithPadding, qrWithPadding, cornerRadius);
        ctx.fill();

        ctx.globalAlpha = 1.0;
        ctx.drawImage(qrImg, x + padding, y + padding, qrSize, qrSize);

        ctx.restore();

        const base64 = canvas.toDataURL('image/png');
        resolve(base64);
      };

      qrImg.onerror = () => {
        reject(new Error('Failed to load QR code image'));
      };

      qrImg.src = qrCodeDataUrl;
    };

    img.onerror = () => {
      reject(new Error('Failed to load cover image'));
    };

    img.src = imageUrl;
  });
};
